import { Form, Modal, Spin, Tabs } from 'antd';
import React, { memo, useCallback, useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import doNothing from '../../../../../../helpers/doNothing';
import queries from '../../../../queries/queries';
import createDescriptionAttachments from '../../../../requests/createRoomImages';
import createFacility from '../../../../requests/createFacility';
import createFacilityTariffs from '../../../../requests/createFacilityTariffs';
import deleteFacility from '../../../../requests/deleteFacility';
import deleteFacilityTariffs from '../../../../requests/deleteFacilityTariffs';
import editFacility from '../../../../requests/editFacility';
import editFacilityTariff from '../../../../requests/editFacilityTariff';
import saveNewAttachments from '../../../../utils/saveNewAttachments';
import AddTariffFields from '../../../AddTariffFields/AddTariffFields';
import FooterButtons from '../../FooterButtons/FooterButtons';
import InformationTab from '../InformationTab/InformationTab';
import { isNumberField } from '../../helpers';

const { confirm } = Modal;

const transformValues = (values) =>
  Object.keys(values).reduce((acc, key) => {
    if (typeof values[key] === 'string') {
      values[key] = values[key].trim();
    }

    acc[key] = values[key];

    return acc;
  }, {});

const FacilityModalNode = ({
  visible,
  onOk,
  onCancel,
  form,
  editingFacility,
}) => {
  const queryClient = useQueryClient();

  const history = useHistory();

  const userCanDelete = useSelector(
    (store) => store.authentication.userInfo.CanDeleteOffice
  );

  const [tariffs, setTariffs] = useState(editingFacility?.tariffs || []);
  const [tab, setTab] = useState('info');
  const [loading, setLoading] = useState(false);
  const [numberError, setNumberError] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(
    editingFacility?.house
  );
  const [imagesList, setImagesList] = useState([]);

  useEffect(() => {
    if (numberError && tab === 'tariffs') {
      setTab('info');
    }
  }, [numberError, tab]);

  useEffect(() => {
    if (editingFacility) {
      setImagesList(
        editingFacility.description?.attachments?.map((file) => {
          file.uid = file.id;
          return file;
        }) || []
      );
    }
  }, [editingFacility]);

  const handleNumberFieldChange = useCallback(() => {
    if (numberError) {
      setNumberError(false);
    }
  }, [numberError]);

  const handleSubmit = (e) => {
    e.preventDefault();
    form.validateFieldsAndScroll(async (err, values) => {
      if (!err) {
        let response;

        const preparedValues = transformValues(values);

        setLoading(true);
        if (!editingFacility) {
          response = await createFacility(preparedValues, setNumberError);

          if (response) {
            if (tariffs?.length > 0) {
              await createFacilityTariffs(tariffs, response.data);
            }

            if (imagesList?.length > 0 && response.data.description?.id) {
              await createDescriptionAttachments(
                response.data.description.id,
                imagesList
              );
            }

            queryClient.invalidateQueries(queries.facilities);
          }
        } else {
          const editedOldImages = imagesList.filter((image) => image.id);
          response = await editFacility(
            {
              ...preparedValues,
              tariffs,
            },
            editingFacility,
            editedOldImages,
            setNumberError
          );
          if (
            editingFacility?.tariffs?.length &&
            editingFacility?.tariffs?.length > tariffs?.length
          ) {
            const deletedTariffs = editingFacility?.tariffs?.filter(
              (tariff) =>
                !tariffs.find((newTariff) => newTariff.id === tariff.id)
            );
            await deleteFacilityTariffs(deletedTariffs);
          }

          if (response) {
            if (tariffs?.length) {
              await Promise.all(
                tariffs.map((tariff) => {
                  if (tariff.id) {
                    return editFacilityTariff(tariff, editingFacility);
                  }
                })
              );
              await createFacilityTariffs(
                tariffs.filter((tariff) => !tariff.id),
                editingFacility
              );
            }

            await saveNewAttachments(
              imagesList,
              createDescriptionAttachments,
              editingFacility.id,
              editingFacility.description.id
            );

            queryClient.invalidateQueries([
              queries.facility,
              editingFacility.id,
            ]);
          }
        }
        setLoading(false);
        if (response) {
          form.resetFields();
          setSelectedAddress();
          setImagesList([]);
          onOk(true);
        }
      } else {
        setTab('info');
      }
    });
  };

  const changeTab = (tab) => {
    setTab(tab);
  };
  const onClose = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);
  const onDelete = useCallback(() => {
    confirm({
      title: `Вы уверены, что хотите удалить объект ${editingFacility.name}?`,
      content: 'Это действие нельзя будет отменить',
      onOk: async () => {
        await deleteFacility(editingFacility.id);
        history.push('/Offices/facilities');
        onClose();
      },
      onCancel() {},
    });
  }, [editingFacility, history, onClose]);
  return (
    <Modal
      title={`${editingFacility ? 'Редактировать' : 'Добавить'} объект`}
      visible={visible}
      onOk={handleSubmit}
      onCancel={loading ? doNothing : onClose}
      width={650}
      footer={
        <FooterButtons
          disabled={loading}
          onOk={handleSubmit}
          onCancel={onClose}
          onDelete={onDelete}
          deleteButtonText="Удалить объект"
          showDelete={editingFacility && userCanDelete}
        />
      }
    >
      <Spin spinning={loading}>
        <Tabs
          defaultActiveKey="info"
          onChange={changeTab}
          activeKey={tab}
          size={'large'}
          tabBarGutter={150}
          style={{ marginTop: '-20px' }}
          tabBarStyle={{ textAlign: 'center' }}
        >
          <Tabs.TabPane tab="Информация" key="info">
            <InformationTab
              numberError={numberError}
              editingFacility={editingFacility}
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              setFieldsValue={form.setFieldsValue}
              getFieldDecorator={form.getFieldDecorator}
              imagesList={imagesList}
              setImagesList={setImagesList}
              handleSubmit={handleSubmit}
              onNumberFieldChange={handleNumberFieldChange}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Тарифы" key="tariffs">
            <AddTariffFields tariffs={tariffs} setTariffs={setTariffs} />
          </Tabs.TabPane>
        </Tabs>
      </Spin>
    </Modal>
  );
};

const FacilityModal = Form.create({ name: 'facility' })(FacilityModalNode);

export default memo(FacilityModal);
